import {
  COLUMN_WIDTH_MAX,
  COLUMN_WIDTH_MIN,
  COMPANY_FIELD_PREFIX,
  LEAD_LIST_FIELDS_MAX,
  LEAD_LIST_INCLUDES,
  tableColumnsSchema,
  type LeadCatalogResponse,
  type LeadSortField,
  type TableColumn,
  type TableScope,
} from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import type { IconName } from "../../design/components/Icon";
import type { Translate } from "../../i18n/locale";
import type { TabKey } from "../../session/tab-context";

// Le colonne dell'elenco Lead e la vista (piano M3 Task 6, D5; mock-up `COLUMNS`, `fallbackLayout`, `normalizeLayout`;
// UX_SYSTEM, «Tabelle e grandi dataset»). Funzioni pure: il catalogo delle colonne (quelle di sistema e una per ogni
// campo dell'azienda, `field:<chiave>`), la vista iniziale del prodotto, la precedenza fra le viste (disposizione non
// salvata del dispositivo → vista personale → vista dell'azienda → vista iniziale) e la disposizione sul dispositivo.
// Una vista nomina le colonne con un identificativo stabile: uno che il catalogo non ha più si scarta in lettura, una
// colonna nuova del catalogo arriva in coda e nascosta, «Cognome» resta sempre prima e visibile. Una colonna non concede
// nulla: i valori arrivano soltanto con le righe che il server già dà.
//
// Dal 6 ottobre 2026 (decisione del product owner: «Questi sono i 4 dati fondamentali») la vista iniziale comincia con
// Cognome, Nome, Cellulare ed Email, poi Nome del salone (questione aperta 455), e la colonna che apre la scheda è «Cognome».
// La colonna «Lead» di prima (nome e cognome insieme, il salone sotto) non c'è più: una vista salvata che la nomina ancora
// la vede sostituita in lettura (`withoutOldLeadColumn`).

/** Le colonne di sistema, con i loro identificativi stabili (quelli che le viste salvano), nell'ordine della vista iniziale. */
export const SYSTEM_COLUMN_IDS = [
  "lastName",
  "firstName",
  "mobile",
  "email",
  "salonName",
  "city",
  "status",
  "latestCall",
  "nextTask",
  "calls",
  "calls12Months",
  "calls4Months",
  "tags",
  "createdAt",
  "owner",
  "landline",
  "province",
  "region",
  "country",
  "latestCallOutcome",
  "source",
  "smartLists",
] as const;
export type SystemColumnId = (typeof SYSTEM_COLUMN_IDS)[number];
export type ColumnId = SystemColumnId | `field:${string}`;
export type SortKind = "text" | "number" | "date" | "order";
export type ColumnGroup = "lead" | "calls" | "territory" | "salon" | "contact";
export type CompanyFieldDef = LeadCatalogResponse["fields"][number];

export type ColumnDef = {
  id: ColumnId;
  /** Il nome nell'intestazione e nel pannello. */
  label: string;
  /** Il nome intero di una colonna che nell'intestazione ne mostra uno breve: suggerimento e didascalia dell'ordinamento. */
  fullLabel?: string;
  icon: IconName;
  group: ColumnGroup;
  /** La larghezza di partenza, in pixel (mock-up `COLUMN_WIDTHS`). */
  width: number;
  /** Un numero: a destra, con le cifre della stessa larghezza. */
  numeric?: boolean;
  sort?: { field: LeadSortField; kind: SortKind };
  /** «Cognome», che apre la scheda: sempre prima e visibile, non si sposta né si nasconde. */
  always?: boolean;
  /** Per un campo dell'azienda: il tipo e le voci, per scrivere il valore. */
  companyField?: CompanyFieldDef;
};

/** I gruppi delle colonne nel pannello, in quest'ordine. */
export const COLUMN_GROUP_ORDER: readonly ColumnGroup[] = ["lead", "calls", "territory", "contact", "salon"];

type SystemColumn = Omit<ColumnDef, "id" | "label" | "fullLabel"> & { id: SystemColumnId; label: MessageKey; fullLabel?: MessageKey };

/** La colonna che apre la scheda: sempre prima e visibile (decisione del 6 ottobre 2026). */
export const FIRST_COLUMN_ID = "lastName" satisfies SystemColumnId;

/**
 * Le colonne di sistema nell'ordine del catalogo. Le larghezze di partenza sono quelle del mock-up (`COLUMN_WIDTHS`);
 * le tre delle chiamate larghe quanto l'intestazione con la freccia dell'ordinamento (a 110 px quella di «Chiamate»
 * restava tagliata). «Prossima task» è la colonna di M2: «—» fino ai Task (M5), senza ordinamento (decisione P9).
 * «Cognome» (con l'iniziale accanto) e «Nome» stanno insieme in 330 px: sul telefono, a 390 px, si leggono tutte e due;
 * «Nome del salone» è largo quanto la riga del salone che prima stava sotto il nome. Cognome e Nome si ordinano come
 * testo; il salone, il cellulare e l'email no, come prima.
 */
const SYSTEM_COLUMNS: readonly SystemColumn[] = [
  { id: "lastName", label: "leadList.columns.lastName", icon: "user", group: "lead", width: 180, sort: { field: "lastName", kind: "text" }, always: true },
  { id: "firstName", label: "leadList.columns.firstName", icon: "user", group: "lead", width: 150, sort: { field: "firstName", kind: "text" } },
  { id: "mobile", label: "leadList.columns.mobile", icon: "phone", group: "contact", width: 170 },
  { id: "email", label: "leadList.columns.email", icon: "note", group: "contact", width: 250 },
  { id: "salonName", label: "leadList.columns.salonName", icon: "report", group: "salon", width: 200 },
  { id: "city", label: "leadList.columns.city", icon: "pin", group: "territory", width: 170, sort: { field: "city", kind: "text" } },
  { id: "status", label: "leadList.columns.status", icon: "dot", group: "lead", width: 170, sort: { field: "status", kind: "order" } },
  { id: "latestCall", label: "leadList.columns.latestCall", icon: "phone", group: "calls", width: 180, sort: { field: "latestCallAt", kind: "date" } },
  { id: "nextTask", label: "leadList.columns.nextTask", icon: "clock", group: "calls", width: 220 },
  {
    id: "calls",
    label: "leadList.columns.calls",
    fullLabel: "leadList.columns.callsFull",
    icon: "report",
    group: "calls",
    width: 132,
    numeric: true,
    sort: { field: "outgoingCallCount", kind: "number" },
  },
  {
    id: "calls12Months",
    label: "leadList.columns.calls12Months",
    fullLabel: "leadList.columns.calls12MonthsFull",
    icon: "report",
    group: "calls",
    width: 180,
    numeric: true,
    sort: { field: "outgoingCallCountLast12Months", kind: "number" },
  },
  {
    id: "calls4Months",
    label: "leadList.columns.calls4Months",
    fullLabel: "leadList.columns.calls4MonthsFull",
    icon: "report",
    group: "calls",
    width: 176,
    numeric: true,
    sort: { field: "outgoingCallCountLast4Months", kind: "number" },
  },
  { id: "tags", label: "leadList.columns.tags", icon: "tag", group: "lead", width: 190 },
  { id: "createdAt", label: "leadList.columns.createdAt", icon: "calendar", group: "lead", width: 170, sort: { field: "createdAt", kind: "date" } },
  { id: "owner", label: "leadList.columns.owner", icon: "user", group: "lead", width: 150, sort: { field: "owner", kind: "text" } },
  { id: "landline", label: "leadList.columns.landline", icon: "phone", group: "contact", width: 170 },
  { id: "province", label: "leadList.columns.province", icon: "pin", group: "territory", width: 150 },
  { id: "region", label: "leadList.columns.region", icon: "pin", group: "territory", width: 150 },
  { id: "country", label: "leadList.columns.country", icon: "pin", group: "territory", width: 120 },
  { id: "latestCallOutcome", label: "leadList.columns.latestCallOutcome", icon: "phone", group: "calls", width: 200 },
  // «Provenienza» e «In Smart List» (LEAD-13): i loro valori arrivano soltanto quando si vedono (`listIncludes`); non si ordinano.
  // Larghe quanto una sorgente con la campagna e quanto tre liste dai nomi brevi.
  { id: "source", label: "leadList.columns.source", icon: "right", group: "lead", width: 240 },
  { id: "smartLists", label: "leadList.columns.smartLists", icon: "smartlist", group: "lead", width: 300 },
];

/** La larghezza di partenza di un campo dell'azienda per tipo (mock-up: «Addetti» 110, «Fiche media» 120). */
const COMPANY_FIELD_WIDTHS: Readonly<Record<CompanyFieldDef["type"], number>> = { number: 120, currency_eur: 130, choice: 170, text: 200 };

/**
 * Le colonne che prendono il posto di «Lead» in una vista di prima, nello stesso ordine della vista iniziale: Cognome e
 * Nome, Cellulare ed Email, Nome del salone.
 */
const OLD_LEAD_REPLACEMENT = ["lastName", "firstName", "mobile", "email", "salonName"] as const satisfies readonly SystemColumnId[];

/**
 * La vista iniziale del prodotto (decisione del 6 ottobre 2026): Cognome, Nome, Cellulare, Email e Nome del salone, poi le
 * undici colonne di prima nell'ordine confermato (24–25 settembre e 1° ottobre 2026, «Prossima task» compresa) senza
 * «Lead», poi nascoste in coda quelle che si accendono da «Colonne e vista». I campi dell'azienda li aggiunge in coda,
 * nascosti, la normalizzazione.
 */
export const PRODUCT_FALLBACK: readonly TableColumn[] = [
  ...([...OLD_LEAD_REPLACEMENT, "city", "status", "latestCall", "nextTask", "calls", "calls12Months", "calls4Months", "tags", "createdAt", "owner"] as const).map(
    (fieldId): TableColumn => ({ fieldId, visible: true }),
  ),
  ...(["landline", "province", "region", "country", "latestCallOutcome", "source", "smartLists"] as const).map((fieldId): TableColumn => ({ fieldId, visible: false })),
];

function companyColumn(field: CompanyFieldDef): ColumnDef {
  return {
    id: `${COMPANY_FIELD_PREFIX}${field.key}`,
    label: field.label,
    icon: "report",
    group: "salon",
    width: COMPANY_FIELD_WIDTHS[field.type],
    ...(field.type === "number" || field.type === "currency_eur" ? { numeric: true } : {}),
    companyField: field,
  };
}

/**
 * Il catalogo delle colonne: quelle di sistema nell'ordine del catalogo, poi una per ogni campo dell'azienda non
 * archiviato (`GET /api/leads/catalog`), con il nome del campo. Senza il catalogo restano quelle di sistema.
 */
export function columnCatalog(t: Translate, catalog: LeadCatalogResponse | undefined): ColumnDef[] {
  const system = SYSTEM_COLUMNS.map(({ label, fullLabel, ...column }): ColumnDef => ({ ...column, label: t(label), ...(fullLabel ? { fullLabel: t(fullLabel) } : {}) }));
  return [...system, ...(catalog?.fields ?? []).map(companyColumn)];
}

/** La forma della chiave di un campo dell'azienda (`publishLeadFieldsBodySchema` dei contratti). */
const COMPANY_FIELD_KEY = /^[a-z][a-z0-9_]{1,40}$/;

/**
 * Mentre il catalogo dei campi dell'azienda arriva (o se la sua lettura è fallita), le colonne `field:<chiave>` che una vista già nomina restano, con la
 * chiave come nome: si vedono subito (e i loro valori si chiedono già con l'elenco), e una modifica della disposizione
 * fatta in quel momento non le perde. Arrivato il catalogo, prendono il nome del campo, o spariscono se non c'è più.
 */
export function withPendingFieldColumns(catalog: readonly ColumnDef[], layouts: ReadonlyArray<readonly TableColumn[] | null>): ColumnDef[] {
  const known = new Set<string>(catalog.map((column) => column.id));
  const pending: ColumnDef[] = [];
  for (const layout of layouts) {
    for (const { fieldId } of layout ?? []) {
      const key = fieldId.slice(COMPANY_FIELD_PREFIX.length);
      // Soltanto una chiave con la forma giusta: una di forma strana non è un campo, e si scarta come gli id sconosciuti.
      if (known.has(fieldId) || !fieldId.startsWith(COMPANY_FIELD_PREFIX) || !COMPANY_FIELD_KEY.test(key)) continue;
      known.add(fieldId);
      pending.push({ id: `${COMPANY_FIELD_PREFIX}${key}`, label: key, icon: "report", group: "salon", width: COMPANY_FIELD_WIDTHS.text });
    }
  }
  return [...catalog, ...pending];
}

export type LayoutSource = "device" | "personal" | "company" | "product";
export type ResolvedLayout = { columns: TableColumn[]; source: LayoutSource };

/** Una larghezza fra 60 e 800 px, in pixel interi (`tableColumnSchema`). */
export function clampColumnWidth(width: number): number {
  return Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(width)));
}

/** La colonna «Lead» delle viste salvate prima del 6 ottobre 2026: nome e cognome insieme, il salone sotto. */
const OLD_LEAD_COLUMN_ID = "lead";

/**
 * Una vista di prima, che nomina ancora «Lead», con le colonne di adesso (decisione del 6 ottobre 2026): «Lead» lascia il
 * posto a Cognome e Nome, seguiti da Cellulare ed Email e da Nome del salone, tutti visibili anche se nella vista erano
 * spenti (lo erano soltanto perché era la partenza); le cinque stanno insieme dove la vista di prima mostrava «Lead», cioè
 * prime, perché «Lead» si vedeva sempre prima dovunque fosse salvata. Ognuna delle cinque tiene la larghezza che la vista
 * le aveva già dato (Cellulare ed Email ne potevano avere una); quella di «Lead» si scarta, perché valeva per un'altra
 * cella: Cognome parte dalla sua. Il resto della vista resta com'era. Una vista senza «Lead» resta com'è.
 */
function withoutOldLeadColumn(columns: readonly TableColumn[]): readonly TableColumn[] {
  if (!columns.some((column) => column.fieldId === OLD_LEAD_COLUMN_ID)) return columns;
  const widths = new Map<string, number>();
  for (const { fieldId, width } of columns) if (width !== undefined && !widths.has(fieldId)) widths.set(fieldId, width);
  const replacement = OLD_LEAD_REPLACEMENT.map((fieldId): TableColumn => {
    const width = widths.get(fieldId);
    return width === undefined ? { fieldId, visible: true } : { fieldId, visible: true, width };
  });
  return [...replacement, ...columns.filter((column) => column.fieldId !== OLD_LEAD_COLUMN_ID)];
}

/**
 * Una vista letta (dal server o dal dispositivo) sul catalogo di adesso: una vista di prima con «Lead» ha al suo posto le
 * colonne di adesso (`withoutOldLeadColumn`); le colonne che il catalogo non ha si scartano, ognuna una volta sola (la
 * prima vince); «Cognome» va prima e visibile; quelle del catalogo che la vista non nomina arrivano in coda, nascoste,
 * nell'ordine del catalogo. Una vista che non nomina nessuna colonna conosciuta oltre a «Cognome» (o a «Lead») non vale
 * (`null`): si usa quella dopo.
 */
export function normalizeColumns(catalog: readonly ColumnDef[], columns: readonly TableColumn[]): TableColumn[] | null {
  const known = new Set<string>(catalog.map((column) => column.id));
  if (!columns.some((column) => known.has(column.fieldId) && column.fieldId !== FIRST_COLUMN_ID)) return null;
  const seen = new Set<string>();
  const kept: TableColumn[] = [];
  for (const column of withoutOldLeadColumn(columns)) {
    if (!known.has(column.fieldId) || seen.has(column.fieldId)) continue;
    seen.add(column.fieldId);
    kept.push({ fieldId: column.fieldId, visible: column.visible, ...(column.width === undefined ? {} : { width: clampColumnWidth(column.width) }) });
  }
  const first = kept.find((column) => column.fieldId === FIRST_COLUMN_ID);
  const rest = kept.filter((column) => column.fieldId !== FIRST_COLUMN_ID);
  const missing = catalog
    .filter((column) => !seen.has(column.id) && column.id !== FIRST_COLUMN_ID)
    .map((column): TableColumn => ({ fieldId: column.id, visible: false }));
  return [{ fieldId: FIRST_COLUMN_ID, visible: true, ...(first?.width === undefined ? {} : { width: first.width }) }, ...rest, ...missing];
}

/**
 * La vista in uso e da dove viene: la disposizione non salvata del dispositivo, poi la vista personale, poi quella
 * dell'azienda, poi la vista iniziale del prodotto (D5). Ognuna si normalizza sul catalogo; una che non vale cede il
 * posto a quella dopo.
 */
export function resolveLayout(input: {
  catalog: readonly ColumnDef[];
  company: readonly TableColumn[] | null;
  personal: readonly TableColumn[] | null;
  device: readonly TableColumn[] | null;
}): ResolvedLayout {
  const levels: Array<[LayoutSource, readonly TableColumn[] | null]> = [
    ["device", input.device],
    ["personal", input.personal],
    ["company", input.company],
  ];
  for (const [source, columns] of levels) {
    const normalized = columns === null ? null : normalizeColumns(input.catalog, columns);
    if (normalized !== null) return { columns: normalized, source };
  }
  // La vista iniziale nomina sempre colonne del catalogo: non è mai `null`.
  return { columns: normalizeColumns(input.catalog, PRODUCT_FALLBACK) ?? [...PRODUCT_FALLBACK], source: "product" };
}

/**
 * La vista di una tabella per chi guarda, dalle viste lette (D5): il catalogo delle colonne (finché i campi dell'azienda
 * non sono arrivati, o se la loro lettura è fallita, con le colonne `field:<chiave>` che una vista già nomina) e la vista
 * in uso con la sua provenienza. Una regola sola per la pagina (`useTableLayout`) e per la lettura in anticipo della
 * prima pagina di una Smart List, che deve chiedere gli stessi campi.
 */
export function resolveTableView(input: {
  t: Translate;
  catalog: LeadCatalogResponse | undefined;
  company: readonly TableColumn[] | null;
  personal: readonly TableColumn[] | null;
  device: readonly TableColumn[] | null;
}): { columns: ColumnDef[]; resolved: ResolvedLayout } {
  const { t, catalog, company, personal, device } = input;
  const base = columnCatalog(t, catalog);
  const columns = catalog ? base : withPendingFieldColumns(base, [device, personal, company]);
  return { columns, resolved: resolveLayout({ catalog: columns, company, personal, device }) };
}

/** Due disposizioni uguali: stesse colonne, nello stesso ordine, con la stessa visibilità e larghezza. */
export function sameColumns(left: readonly TableColumn[], right: readonly TableColumn[]): boolean {
  return (
    left.length === right.length &&
    left.every((column, index) => {
      const other = right[index];
      return other !== undefined && other.fieldId === column.fieldId && other.visible === column.visible && other.width === column.width;
    })
  );
}

/** Le colonne visibili, nell'ordine della vista, con la loro definizione: quelle che la tabella disegna. */
export function visibleColumnDefs(catalog: readonly ColumnDef[], columns: readonly TableColumn[]): ColumnDef[] {
  const byId = new Map<string, ColumnDef>(catalog.map((column) => [column.id, column]));
  return columns.flatMap((column) => {
    const def = column.visible ? byId.get(column.fieldId) : undefined;
    return def ? [def] : [];
  });
}

/** Le larghezze scelte, colonna per colonna; le altre usano quella di partenza. */
export function columnWidths(columns: readonly TableColumn[]): Record<string, number> {
  const widths: Record<string, number> = {};
  for (const column of columns) if (column.width !== undefined) widths[column.fieldId] = column.width;
  return widths;
}

/** Mostra o nasconde una colonna, al suo posto nell'ordine; «Cognome» resta visibile. */
export function setColumnVisible(columns: readonly TableColumn[], fieldId: string, visible: boolean): TableColumn[] {
  if (fieldId === FIRST_COLUMN_ID) return [...columns];
  return columns.map((column) => (column.fieldId === fieldId ? { ...column, visible } : column));
}

/**
 * Sposta una colonna visibile al posto `toIndex` fra le colonne visibili (0 è «Cognome», che non si sposta e resta prima);
 * le nascoste restano dove sono rispetto alle altre.
 */
export function moveVisibleColumn(columns: readonly TableColumn[], fieldId: string, toIndex: number): TableColumn[] {
  const moving = columns.find((column) => column.fieldId === fieldId);
  if (!moving || fieldId === FIRST_COLUMN_ID || !moving.visible) return [...columns];
  const rest = columns.filter((column) => column.fieldId !== fieldId);
  const visible = rest.filter((column) => column.visible);
  const target = Math.max(1, Math.min(toIndex, visible.length));
  const before = visible[target - 1];
  const position = before ? rest.indexOf(before) + 1 : 1;
  return [...rest.slice(0, position), moving, ...rest.slice(position)];
}

/** La larghezza di una colonna, fra 60 e 800 px. */
export function setColumnWidth(columns: readonly TableColumn[], fieldId: string, width: number): TableColumn[] {
  return columns.map((column) => (column.fieldId === fieldId ? { ...column, width: clampColumnWidth(width) } : column));
}

/**
 * Le chiavi dei campi dell'azienda visibili, per `GET /api/leads?fields=`: soltanto quelle con la forma di una chiave
 * (una vista del dispositivo o del server di forma strana non arriva mai alla query, che la rifiuterebbe con un `422`),
 * in ordine alfabetico, così spostare una colonna non rilegge l'elenco; al massimo venti (`LEAD_LIST_FIELDS_MAX`).
 */
export function companyFieldKeys(columns: readonly TableColumn[]): string[] {
  return columns
    .filter((column) => column.visible && column.fieldId.startsWith(COMPANY_FIELD_PREFIX))
    .map((column) => column.fieldId.slice(COMPANY_FIELD_PREFIX.length))
    .filter((key) => COMPANY_FIELD_KEY.test(key))
    .sort()
    .slice(0, LEAD_LIST_FIELDS_MAX);
}

/**
 * Le colonne di sistema visibili che l'elenco chiede con `GET /api/leads?include=` («In Smart List» e «Provenienza», LEAD-13: il
 * loro identificativo è la chiave del contratto), nell'ordine del contratto, così spostare una colonna non rilegge l'elenco.
 */
export function listIncludes(columns: readonly TableColumn[]): string {
  const visible = new Set(columns.filter((column) => column.visible).map((column) => column.fieldId));
  return LEAD_LIST_INCLUDES.filter((include) => visible.has(include)).join(",");
}

/** Le colonne da salvare: tutte, ma al massimo 80 (`tableColumnsSchema`), lasciando fuori le nascoste in più. */
export function columnsToSave(columns: readonly TableColumn[]): TableColumn[] {
  const MAX = 80;
  if (columns.length <= MAX) return [...columns];
  let hiddenRoom = MAX - columns.filter((column) => column.visible).length;
  return columns.filter((column) => {
    if (column.visible) return true;
    hiddenRoom -= 1;
    return hiddenRoom >= 0;
  });
}

/** Le parole dell'ordinamento per tipo di colonna, come nel mock-up (`SORT_WORDS`). */
export const SORT_WORDS: Readonly<Record<SortKind, { asc: MessageKey; desc: MessageKey }>> = {
  text: { asc: "leadList.sortWords.textAsc", desc: "leadList.sortWords.textDesc" },
  number: { asc: "leadList.sortWords.numberAsc", desc: "leadList.sortWords.numberDesc" },
  date: { asc: "leadList.sortWords.dateAsc", desc: "leadList.sortWords.dateDesc" },
  order: { asc: "leadList.sortWords.orderAsc", desc: "leadList.sortWords.orderDesc" },
};

export type LeadSort = { field: LeadSortField; direction: "asc" | "desc" };

/** L'ordinamento di partenza dell'elenco Lead, come il server: data di creazione, dalla più recente. */
export const DEFAULT_LEAD_SORT: LeadSort = { field: "createdAt", direction: "desc" };

/** Due ordinamenti uguali: stessa colonna, stesso verso. */
export const sameSort = (left: LeadSort, right: LeadSort): boolean => left.field === right.field && left.direction === right.direction;

/**
 * L'ordinamento della vista salvata in uso (LEAD-17): quello della vista personale, se ne ha uno, altrimenti quello della vista
 * dell'azienda, altrimenti quello di partenza. Una vista personale senza ordinamento (salvata prima che la vista lo portasse) non
 * toglie quello dell'azienda.
 */
export function savedSortOf(server: { company: { sort?: LeadSort | null } | null; personal: { sort?: LeadSort | null } | null } | undefined): LeadSort {
  return server?.personal?.sort ?? server?.company?.sort ?? DEFAULT_LEAD_SORT;
}

/**
 * Gli ordinamenti che non hanno più una colonna: `lead`, nome e cognome insieme, della colonna «Lead» di prima, che una
 * Smart List salvata o un indirizzo possono ancora avere (decisione del 6 ottobre 2026). La didascalia lo dice come prima.
 */
const SORTS_WITHOUT_COLUMN: Partial<Record<LeadSortField, { label: MessageKey; kind: SortKind }>> = {
  lead: { label: "leadList.columns.lead", kind: "text" },
};

/**
 * La colonna e le parole di un ordinamento, per la didascalia: «data di creazione, dal più recente». La colonna si cerca
 * nel catalogo, non fra quelle visibili: nascondere la colonna non cambia l'ordinamento, e la didascalia lo dice ancora.
 */
export function sortDescription(t: Translate, catalog: readonly ColumnDef[], sort: LeadSort): { column: string; direction: string } {
  const column = catalog.find((candidate) => candidate.sort?.field === sort.field);
  if (!column?.sort) {
    const old = SORTS_WITHOUT_COLUMN[sort.field];
    return old ? { column: t(old.label).toLocaleLowerCase(), direction: t(SORT_WORDS[old.kind][sort.direction]) } : { column: sort.field, direction: "" };
  }
  return { column: (column.fullLabel ?? column.label).toLocaleLowerCase(), direction: t(SORT_WORDS[column.sort.kind][sort.direction]) };
}

// ---------------------------------------------------------------------------------------------------------------
// La disposizione non salvata, soltanto su questo dispositivo (D5): `localStorage`, una voce per azienda e contesto.

/** Il contesto della vista dell'elenco Lead: uno per azienda, senza identificativo. */
export const LEADS_TABLE_SCOPE: TableScope = { scopeType: "leads", scopeId: "" };

/** La parte del contesto nella chiave: `leads`, oppure `smart_list:<id>`. */
export function tableScopeKey(scope: TableScope): string {
  return scope.scopeType === "leads" ? "leads" : `smart_list:${scope.scopeId}`;
}

/** La chiave della disposizione sul dispositivo: `gioia365.vista.<tabKey>.<contesto>`. */
export function deviceLayoutKey(tab: TabKey, scopeKey: string): string {
  return `gioia365.vista.${tab}.${scopeKey}`;
}

/**
 * Lo `localStorage` del browser, oppure `null` dove leggerlo è vietato (alcune navigazioni private, cookie bloccati):
 * allora la disposizione vale finché la pagina resta aperta.
 */
export function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** La disposizione salvata sul dispositivo, se c'è e ha una forma valida; un errore di lettura vale come nessuna. */
export function readDeviceLayout(storage: Storage | null, tab: TabKey, scopeKey: string): TableColumn[] | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(deviceLayoutKey(tab, scopeKey));
    if (raw === null) return null;
    const parsed = tableColumnsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Scrive (o toglie, con `null`) la disposizione sul dispositivo; uno storage pieno o vietato non rompe la pagina. */
export function writeDeviceLayout(storage: Storage | null, tab: TabKey, scopeKey: string, columns: readonly TableColumn[] | null): void {
  if (!storage) return;
  try {
    const key = deviceLayoutKey(tab, scopeKey);
    if (columns === null) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(columns));
  } catch {
    // Senza archivio la disposizione vale finché la pagina resta aperta.
  }
}
